/* ───────────────────────────────────────────────────────────────────────────
   O16 PDP gallery video gate — stop hidden/off-screen autoplay video from
   streaming on load.

   MEASURED (live, Instagram in-app browser, iPhone viewport,
   /products/padella-pan-o24?variant=54714011189577):
     - The page pulled 56 MB over 311 requests, of which 49 MB was video.
     - 10 <video> elements existed in the DOM — the product section renders
       TWICE (desktop wrapper + mobile wrapper: sections/main-product.liquid
       wraps snippets/main-product-section.liquid, which renders
       snippets/o16-product-gallery.liquid, in BOTH
       `.main-product-desktop-section-wrapper` AND
       `.main-product-mobile-section-wrapper`), so the same 5 gallery videos
       existed twice.
     - Every one of the 10 had `autoplay` + `preload="none"`, and NONE was
       visible (`offsetParent === null`), yet every one reported
       `readyState: 4` with 3-8s buffered. WebKit starts fetching an autoplay
       video regardless of preload="none", hidden or not.
     - Net result: 5 unique files fetched 28 times, 46 MB, before the
       shopper had done anything.

   FIX, two parts:
     1. snippets/o16-product-gallery.liquid ("MOTION ITEMS") now renders its
        `media_tag` with `autoplay: false`. That is the actual video markup
        for the PDP gallery's Motion tab — confirmed by grep: it is the only
        place in the render path of templates/product.json that outputs a
        native <video> tag with autoplay for a gallery item.
        (snippets/product-thumbnail.liquid has its own autoplay:true video
        line, but that snippet is only rendered from sections/featured-
        product.liquid, which templates/product.json does not use — it is
        not part of this page at all and was left untouched.)
     2. THIS FILE replaces the native autoplay attribute with a viewport
        gate, so a gallery video still plays like a shopper expects once it
        is genuinely on screen, and stops the instant it isn't — instead of
        every copy of every video streaming at once regardless of visibility.

   SCOPE: only <video> elements inside the <o16-product-gallery> custom
   element (assets/o16-product-gallery.js / snippets/o16-product-gallery.
   liquid) — i.e. the PDP gallery's own Motion-tab videos. This element is
   rendered exactly twice per page (desktop + mobile product-section
   wrappers) and nowhere else, so this never touches:
     - the media modal / lightbox (snippets/main-product-section.liquid's
       `product-media-modal__item` markup, a different element tree, whose
       videos live inert inside a <template> until the shopper opens the
       modal),
     - the cart drawer,
     - o16-media-banner-autoplay.js's home-page banner videos (a different
       gate, left as-is — see that file for its own viewport logic),
     - the external_video_url (YouTube/Vimeo) embeds one case above the
       native-video line in o16-product-gallery.liquid — those render an
       <iframe>, not a <video>, so the `video` selector below never matches
       them.

   RULE (do not re-enable native autoplay on the gallery video markup): a
   video only ever plays here when BOTH (a) it is actually rendered
   (`offsetParent !== null` — this is what excludes whichever copy, desktop
   or mobile, is the CSS-hidden one) AND (b) an IntersectionObserver reports
   it at least 50% on screen. It pauses (never resets currentTime) the
   moment either condition stops holding.

   Wakeable like window.O16StickyBuybar.init / window.O16PdpTitleFit.refit /
   window.O16MobilePriceSync.init: the bundle ladder's page takeover
   (assets/o16-bundle-ladder.js) replaces the whole product section, so
   window.O16PdpVideoGate.init() must be callable again afterwards without
   stacking a second IntersectionObserver on top of a now-detached one.
   ─────────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  var observer = null;

  function safePlay(video) {
    if (!video.paused) return;
    var promise = video.play();
    if (!promise || typeof promise.catch !== 'function') return; // older Safari — no return value
    promise.catch(function () {
      // WebKit rejects play() without user activation in some contexts
      // (e.g. the tab briefly regains 50% visibility during a fast scroll
      // fling before the shopper has interacted with the page at all).
      // Never let that surface as an unhandled rejection — the video just
      // stays on its poster until the next qualifying intersection or a
      // gesture elsewhere unlocks autoplay for the page.
    });
  }

  function safePause(video) {
    if (!video.paused) video.pause();
  }

  function handleIntersections(entries) {
    entries.forEach(function (entry) {
      var video = entry.target;
      var onScreen = entry.isIntersecting && video.offsetParent !== null;
      if (onScreen) {
        safePlay(video);
      } else {
        safePause(video);
      }
    });
  }

  function init() {
    // Re-bindable — disconnect first so a re-init after a page takeover can
    // never leave a stale observer watching detached video nodes while a
    // fresh one also watches the replacement markup.
    if (observer) {
      observer.disconnect();
      observer = null;
    }

    var reducedMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) return; // never auto-play at all in this case

    if (!('IntersectionObserver' in window)) return; // no gate available — video stays on its poster (no native autoplay attribute either)

    var videos = document.querySelectorAll('o16-product-gallery video');
    if (!videos.length) return;

    observer = new IntersectionObserver(handleIntersections, { threshold: 0.5 });
    videos.forEach(function (video) {
      observer.observe(video);
    });
  }

  // Same contract as O16StickyBuybar.init / O16PdpTitleFit.refit /
  // O16MobilePriceSync.init — the bundle ladder calls this after a page
  // takeover; this file does not wire that call itself.
  window.O16PdpVideoGate = { init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
